'use client';

import * as AccordionPrimitive from '@radix-ui/react-accordion';
import * as React from 'react';

import { cn } from '@/lib/utils';
import { classnameMerge } from '@/util/classname-merge';

const Accordion = AccordionPrimitive.Root;

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={className}
    {...props}
  />
));

AccordionItem.displayName = 'AccordionItem';

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> & {
    arrowClassName?: string;
    headerAsChild?: boolean;
  }
>(({ className, arrowClassName, headerAsChild, children, ...props }, ref) => (
  <AccordionPrimitive.Header
    asChild={headerAsChild}
    className='flex'
  >
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        'flex flex-1 items-center justify-between font-medium transition-all [&[data-state=open]>svg]_rotate-180',
        className,
      )}
      {...props}
    >
      {children}
      <svg
        width='16'
        height='10'
        viewBox='0 0 16 10'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
        className={classnameMerge('duration-200 block h-4 w-4 shrink-0 transition-transform', arrowClassName)}
      >
        <path
          d='M15.5006 1.99331C15.5012 2.18542 15.4665 2.37524 15.3991 2.54882C15.3318 2.72239 15.2335 2.87531 15.1114 2.99634L8.80036 9.20746C8.61215 9.3966 8.37607 9.5 8.13243 9.5C7.8888 9.5 7.65272 9.3966 7.46451 9.20746L1.15342 2.77773C0.938615 2.55946 0.803532 2.2458 0.777888 1.90577C0.752244 1.56573 0.83814 1.22716 1.01668 0.964551C1.19522 0.70194 1.45178 0.536794 1.72991 0.505443C2.00805 0.474091 2.28498 0.579103 2.49979 0.797378L8.13769 6.54556L13.7756 0.99027C13.93 0.833021 14.118 0.733132 14.3174 0.702424C14.5168 0.671717 14.7192 0.711474 14.9006 0.816993C15.0821 0.922512 15.2351 1.08938 15.3414 1.29784C15.4477 1.50631 15.503 1.74765 15.5006 1.99331Z'
          fill='currentColor'
        />
      </svg>
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
));

AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className='overflow-hidden text-sm transition-all data-[state=closed]_hidden'
    {...props}
  >
    <div className={className}>{children}</div>
  </AccordionPrimitive.Content>
));

AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger };
